import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'store/store'
import { useLocation, useNavigate } from 'react-router-dom'
import { EAppRoutes } from 'types/enums/EAppRoutes'
import { ESignupFormSteps } from 'types/enums/ESignupFormSteps'
import {
  setIsAuthenticated,
  setIsFanzaErrorExists,
  updateUser,
  userAuth,
} from 'store/reducers/userReducer'
import { setForm, setIsLayoutLoading } from 'store/reducers/layoutReducer'
import { store } from 'store/store'
import { EStatusCodes } from 'types/enums/EStatusCodes'
import { EPersonalDetailsFormSteps } from 'types/enums/EPersonalDetailsFormSteps'
import {
  isFanzaUserAuthenticatedDetails,
  isUserAuthenticatedDetails,
} from 'services/AuthService'

export const useIsAuthenticated = () => {
  const formStep = useAppSelector((state) => state.layout?.formStep)
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated)
  const isCognitoSignedIn = useAppSelector(
    (state) => state.user.isCognitoSignedIn,
  )

  const fetchAuthDetails = async (errorHandling = false) => {
    try {
      const details = await isUserAuthenticatedDetails()
      dispatch(userAuth(details))
    } catch (error: any) {
      console.error('Failed to fetch authentication details:', error)
      dispatch(setIsAuthenticated(false))
      if (errorHandling) {
        switch (error.status) {
          case EStatusCodes.NOT_FOUND:
            dispatch(
              setForm({
                formStep: ESignupFormSteps.personalDetails,
                personalDetailsStep: EPersonalDetailsFormSteps.personalInfo,
              }),
            )
            navigate(EAppRoutes.SIGN_UP)
        }
      }
    } finally {
      dispatch(setIsLayoutLoading(false))
    }
  }

  const fetchFanzaDetails = async (errorHandling = false) => {
    try {
      const getFanzaUserResponse = await isFanzaUserAuthenticatedDetails()
      if (!getFanzaUserResponse) return
      dispatch(
        updateUser({
          isAuthenticated: true,
          newsletter: getFanzaUserResponse.newsletter,
          fanzaRedirectUrl: getFanzaUserResponse.fanzaRedirectUrl,
          personalDetails: {
            seenMarketingMessage: getFanzaUserResponse.seenMarketingMessage,
          },
        }),
      )
    } catch (error) {
      console.error('Failed to fetch authentication details:', error)
      dispatch(setIsAuthenticated(false))
      if (errorHandling) {
        store.dispatch(setIsFanzaErrorExists(true))
      }
    } finally {
      dispatch(setIsLayoutLoading(false))
    }
  }

  useEffect(() => {
    const isSignUpFlow =
      location?.pathname === EAppRoutes.SIGN_UP &&
      formStep === ESignupFormSteps.verificationOption
    if (isSignUpFlow) return

    if (isCognitoSignedIn) {
      dispatch(setIsLayoutLoading(true))
      switch (location.pathname) {
        case '/fanza': {
          fetchFanzaDetails(true)
          break
        }
        default:
          fetchAuthDetails(true)
      }
    }
  }, [isCognitoSignedIn])

  useEffect(() => {
    const isSignUpFlow =
      location?.pathname === EAppRoutes.SIGN_UP &&
      formStep === ESignupFormSteps.verificationOption
    if (isSignUpFlow) return

    dispatch(setIsLayoutLoading(true))
    switch (location.pathname) {
      case '/fanza': {
        fetchFanzaDetails()
        break
      }
      default:
        fetchAuthDetails()
    }
  }, [])

  return isAuthenticated
}
